import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  Lock,
} from "@material-ui/icons";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useAppState } from "../../components/AppProvider/AppProvider";
import { initialRole } from "../../helpers/modelinitials";
import { toArray } from "../../helpers/fetchers";
import { blueTheme } from "../../helpers/themes";
import { Permission, Role } from "../../model";
import { usePermission } from "../../components/Permission/Permission";
import { PERMISSIONS } from "../../helpers/permissions";

type RoleFormMode = "create" | "edit";

const emptyRole = (): Role => ({
  ...initialRole,
  name: "",
  code: "",
  permissions: [],
});

const normalizeCode = (value: string) =>
  value.toUpperCase().replace(/[^A-Z0-9_]/g, "_");

const RBAC_ENFORCEMENT_CONFIG_KEY = "rbac.enforcement.enabled";

const Roles = () => {
  const [ctx] = useAppState() ?? [];
  const canManage = usePermission(PERMISSIONS.ROLES_MANAGE);

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleFormMode, setRoleFormMode] = useState<RoleFormMode>("create");
  const [roleForm, setRoleForm] = useState<Role>(emptyRole());

  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionRole, setPermissionRole] = useState<Role | null>(null);
  const [selectedPermissionCodes, setSelectedPermissionCodes] = useState<string[]>([]);

  const [enforcementEnabled, setEnforcementEnabled] = useState<boolean>(
    Boolean(ctx?.rbacEnabled)
  );
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const baseUrl = ctx?.baseUrl || process.env.REACT_APP_BASE_URL || "";
      const apiKey = ctx?.apiKey || localStorage.getItem("apiKey") || "";
      const [roleResponse, permissionResponse] = await Promise.all([
        fetch(`${baseUrl}/roles`, {
          headers: { authorization: apiKey },
        }),
        fetch(`${baseUrl}/permissions`, {
          headers: { authorization: apiKey },
        }),
      ]);
      if (!roleResponse.ok) throw new Error(await roleResponse.text());
      if (!permissionResponse.ok) throw new Error(await permissionResponse.text());
      const roleData = toArray<Role>(await roleResponse.json());
      const permissionData = toArray<Permission>(await permissionResponse.json());
      setRoles(roleData.map((role) => ({ ...role, permissions: role.permissions || [] })));
      setPermissions(permissionData);
    } catch (error) {
      console.error(error);
      setMessage("Failed to load access management data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRoles = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return roles;
    return roles.filter((role) =>
      `${role.name} ${role.code}`.toLowerCase().includes(keyword)
    );
  }, [roles, search]);

  const permissionGroups = useMemo(() => {
    const groups = permissions.reduce<Record<string, Permission[]>>((result, permission) => {
      const feature = permission.feature || "Other";
      result[feature] = [...(result[feature] || []), permission];
      return result;
    }, {});
    return Object.entries(groups).map(([feature, items]) => ({
      feature,
      permissions: items,
    }));
  }, [permissions]);

  const visibleRoles = filteredRoles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const openCreateRole = () => {
    setRoleFormMode("create");
    setRoleForm(emptyRole());
    setRoleDialogOpen(true);
  };

  const openEditRole = (role: Role) => {
    setRoleFormMode("edit");
    setRoleForm({ ...role, permissions: role.permissions || [] });
    setRoleDialogOpen(true);
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      setMessage("Role name is required.");
      return;
    }
    if (!roleForm.code.trim()) {
      setMessage("Role code is required.");
      return;
    }

    setSavingRole(true);
    try {
      const response = await fetch(`${ctx?.baseUrl}/roles`, {
        method: "POST",
        headers: {
          authorization: ctx?.apiKey ?? "",
          "content-type": "application/json",
        },
        body: JSON.stringify(roleForm),
      });
      if (!response.ok) throw new Error(await response.text());
      setRoleDialogOpen(false);
      setMessage(roleFormMode === "create" ? "Role created successfully." : "Role updated successfully.");
      await load();
    } catch (error) {
      console.error(error);
      setMessage("Failed to save role.");
    } finally {
      setSavingRole(false);
    }
  };

  const deleteRole = async (role: Role) => {
    if (role.isSystemRole) {
      setMessage("System roles cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;

    try {
      const response = await fetch(`${ctx?.baseUrl}/roles/${role.id}`, {
        method: "DELETE",
        headers: { authorization: ctx?.apiKey ?? "" },
      });
      if (!response.ok) throw new Error(await response.text());
      setMessage("Role deleted successfully.");
      await load();
    } catch (error) {
      console.error(error);
      setMessage("Failed to delete role.");
    }
  };

  const openPermissions = (role: Role) => {
    setPermissionRole(role);
    setSelectedPermissionCodes((role.permissions || []).map((permission) => permission.code));
    setPermissionDialogOpen(true);
  };

  const isSelected = (code: string) => selectedPermissionCodes.includes(code);

  const togglePermission = (code: string) => {
    setSelectedPermissionCodes((current) =>
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code]
    );
  };

  const toggleFeature = (items: Permission[]) => {
    const codes = items.map((permission) => permission.code);
    const allSelected = codes.every(isSelected);
    setSelectedPermissionCodes((current) =>
      allSelected
        ? current.filter((code) => !codes.includes(code))
        : [...current, ...codes.filter((code) => !current.includes(code))]
    );
  };

  const savePermissions = async () => {
    if (!permissionRole) return;
    setSavingPermissions(true);
    try {
      const response = await fetch(`${ctx?.baseUrl}/roles/${permissionRole.id}/permissions`, {
        method: "PUT",
        headers: {
          authorization: ctx?.apiKey ?? "",
          "content-type": "application/json",
        },
        body: JSON.stringify({ permissions: selectedPermissionCodes }),
      });
      if (!response.ok) throw new Error(await response.text());
      setPermissionDialogOpen(false);
      setMessage("Role permissions updated successfully.");
      await load();
    } catch (error) {
      console.error(error);
      setMessage("Failed to update role permissions.");
    } finally {
      setSavingPermissions(false);
    }
  };

  const writeEnforcement = async (next: boolean) => {
    try {
      const response = await fetch(`${ctx?.baseUrl}/configuration-values`, {
        method: "POST",
        headers: {
          authorization: ctx?.apiKey ?? "",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          configKey: RBAC_ENFORCEMENT_CONFIG_KEY,
          value: next ? "true" : "false",
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      setEnforcementEnabled(next);
      setMessage(
        next
          ? "RBAC enforcement enabled. Users must re-login for it to take effect."
          : "RBAC enforcement disabled (legacy mode)."
      );
    } catch (error) {
      console.error(error);
      setMessage("Failed to change RBAC enforcement.");
    }
  };

  const onToggleEnforcement = (next: boolean) => {
    if (next) setConfirmEnableOpen(true);
    else writeEnforcement(false);
  };

  return (
    <ThemeProvider theme={blueTheme}>
      <Box m={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h5">Access Management</Typography>
            <Typography variant="body2" color="textSecondary">
              Manage roles and role permissions.
            </Typography>
          </Box>
          {canManage && (
            <Box display="flex" alignItems="center">
              <Box display="flex" alignItems="center" ml={2}>
                <Typography variant="body2" style={{ marginRight: 8 }}>
                  RBAC Enforcement
                </Typography>
                <Checkbox
                  checked={enforcementEnabled}
                  onChange={(e) => onToggleEnforcement(e.target.checked)}
                  color="primary"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={openCreateRole}
              >
                Add Role
              </Button>
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={2}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        ) : roles.length === 0 ? (
          <Paper>
            <Box py={8} textAlign="center" color="text.secondary">
              <Typography variant="h6">No roles yet</Typography>
              <Typography variant="body2">Create a role before assigning permissions.</Typography>
            </Box>
          </Paper>
        ) : (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 48 }} />
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>System Role</TableCell>
                  <TableCell>Permissions</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRoles.map((role) => {
                  const rowKey = `${role.id || role.code || role.name}`;
                  const rolePermissions = role.permissions || [];
                  return (
                    <Fragment key={rowKey}>
                      <TableRow key={rowKey}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpanded((current) => ({
                                ...current,
                                [rowKey]: !current[rowKey],
                              }))
                            }
                          >
                            {expanded[rowKey] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {role.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>{role.code || "-"}</TableCell>
                        <TableCell>
                          {role.isSystemRole ? <Chip size="small" label="System" color="primary" /> : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={rolePermissions.length} />
                        </TableCell>
                        {canManage && (
                          <TableCell align="right">
                            <Tooltip title="Manage permissions">
                              <IconButton size="small" onClick={() => openPermissions(role)}>
                                <Lock fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit role">
                              <IconButton size="small" onClick={() => openEditRole(role)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={role.isSystemRole ? "System roles cannot be deleted" : "Delete role"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={role.isSystemRole}
                                  onClick={() => deleteRole(role)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow key={`${rowKey}-detail`}>
                        <TableCell colSpan={canManage ? 6 : 5} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={Boolean(expanded[rowKey])} timeout="auto" unmountOnExit>
                            <Box p={2} bgcolor="#f5f7fa">
                              <Typography variant="subtitle2">
                                Permissions for {role.name || "Role"}
                              </Typography>
                              {rolePermissions.length === 0 ? (
                                <Box mt={1}>
                                  <Typography variant="body2" color="textSecondary">
                                    No permissions assigned.
                                  </Typography>
                                </Box>
                              ) : (
                                <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                  {rolePermissions.map((permission) => (
                                    <Tooltip key={permission.code} title={permission.code}>
                                      <Chip size="small" label={permission.name} />
                                    </Tooltip>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRoles.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onChangePage={(_, nextPage) => setPage(nextPage)}
              onChangeRowsPerPage={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
            />
          </Paper>
        )}
      </Box>

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{roleFormMode === "create" ? "Create Role" : "Edit Role"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 16 }} pt={1}>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              required
              value={roleForm.name}
              onChange={(event) =>
                setRoleForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
            <TextField
              label="Code"
              variant="outlined"
              size="small"
              required
              disabled={roleForm.isSystemRole}
              value={roleForm.code || ""}
              onChange={(event) =>
                setRoleForm((current) => ({
                  ...current,
                  code: normalizeCode(event.target.value),
                }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={savingRole}
            onClick={saveRole}
          >
            {savingRole ? "Saving..." : roleFormMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={permissionDialogOpen}
        onClose={() => setPermissionDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Permissions for {permissionRole?.name || "Role"}
        </DialogTitle>
        <DialogContent dividers style={{ maxHeight: "65vh" }}>
          {permissionGroups.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="textSecondary">No permissions available.</Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
              {permissionGroups.map((group) => {
                const allSelected = group.permissions.every((permission) =>
                  isSelected(permission.code)
                );
                return (
                  <Paper key={group.feature} variant="outlined">
                    <Box p={2}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Checkbox
                          color="primary"
                          checked={allSelected}
                          indeterminate={
                            !allSelected &&
                            group.permissions.some((permission) => isSelected(permission.code))
                          }
                          onChange={() => toggleFeature(group.permissions)}
                        />
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                          {group.feature}
                        </Typography>
                      </Box>
                      <Grid container spacing={1}>
                        {group.permissions.map((permission) => (
                          <Grid item xs={12} sm={6} md={4} key={permission.code}>
                            <Box display="flex" alignItems="center">
                              <Checkbox
                                color="primary"
                                checked={isSelected(permission.code)}
                                onChange={() => togglePermission(permission.code)}
                              />
                              <Tooltip title={permission.code}>
                                <Typography variant="body2">{permission.name}</Typography>
                              </Tooltip>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={savingPermissions}
            onClick={savePermissions}
          >
            {savingPermissions ? "Saving..." : "Save Permissions"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmEnableOpen} onClose={() => setConfirmEnableOpen(false)}>
        <DialogTitle>Enable RBAC enforcement?</DialogTitle>
        <DialogContent>
          <Typography>
            Users without mapped permissions will lose access until an admin
            assigns them. Everyone must re-login for the change to take effect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnableOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setConfirmEnableOpen(false);
              writeEnforcement(true);
            }}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={1800}
        onClose={() => setMessage("")}
        message={message}
      />
    </ThemeProvider>
  );
};

export default Roles;
